import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { formatCurrency } from '@outlaw-oasis/utils';

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get(`/orders/${id}`),
    enabled: !!id,
  });

  const order = (data as any)?.data?.order;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const copyAllKeys = (items: any[]) => {
    const allKeys = items
      .filter(item => item.keys && item.keys.length > 0)
      .map(item => `${item.productName}:\n${item.keys.join('\n')}`)
      .join('\n\n');

    if (allKeys) {
      navigator.clipboard.writeText(allKeys);
      toast.success('All keys copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="mb-8">
          <Link to="/dashboard/orders" className="text-gold hover:text-gold-bright">
            &larr; Back to Orders
          </Link>
        </div>
        <div className="animate-pulse">
          <div className="h-8 bg-leather rounded w-1/3 mb-8" />
          <div className="card p-6">
            <div className="h-6 bg-leather rounded w-1/4 mb-4" />
            <div className="h-4 bg-leather rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <div className="mb-8">
          <Link to="/dashboard/orders" className="text-gold hover:text-gold-bright">
            &larr; Back to Orders
          </Link>
        </div>
        <div className="card p-8 text-center">
          <h1 className="font-western text-2xl text-cream mb-4">Order Not Found</h1>
          <p className="text-text-secondary">This order doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  const hasAnyKeys = order.items.some((item: any) => item.keys && item.keys.length > 0);

  return (
    <div>
      <div className="mb-8">
        <Link to="/dashboard/orders" className="text-gold hover:text-gold-bright">
          &larr; Back to Orders
        </Link>
      </div>

      <div className="flex items-center justify-between mb-8">
        <h1 className="font-western text-3xl text-cream">
          Order #{order.orderNumber}
        </h1>
        <span className={`px-3 py-1 rounded ${
          order.status === 'completed' ? 'bg-success/20 text-success' :
          order.status === 'pending_payment' ? 'bg-gold/20 text-gold' :
          order.status === 'failed' ? 'bg-error/20 text-error' :
          'bg-text-muted/20 text-text-muted'
        }`}>
          {order.status === 'pending_payment' ? 'Pending Payment' :
           order.status === 'completed' ? 'Completed' :
           order.status === 'failed' ? 'Failed' :
           order.status}
        </span>
      </div>

      {/* Payment Pending Warning */}
      {order.paymentStatus !== 'paid' && (
        <div className="card p-6 mb-6 border-gold/30 bg-gold/5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">&#9888;</span>
            <div>
              <h3 className="text-gold font-semibold">Payment Processing</h3>
              <p className="text-text-secondary text-sm">
                {order.paymentMethod === 'crypto'
                  ? 'Waiting for blockchain confirmation. Your keys will be available once the payment is confirmed.'
                  : 'Your payment is being processed. Your keys will be available shortly.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Order Summary */}
      <div className="card p-6 mb-6">
        <h2 className="font-semibold text-cream mb-4">Order Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-text-muted block">Date</span>
            <span className="text-cream">{new Date(order.createdAt).toLocaleDateString()}</span>
          </div>
          <div>
            <span className="text-text-muted block">Payment Method</span>
            <span className="text-cream capitalize">{order.paymentMethod}</span>
          </div>
          <div>
            <span className="text-text-muted block">Payment Status</span>
            <span className={order.paymentStatus === 'paid' ? 'text-success' : 'text-gold'}>
              {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
            </span>
          </div>
          <div>
            <span className="text-text-muted block">Total</span>
            <span className="text-gold font-bold">{formatCurrency(order.total)}</span>
          </div>
        </div>
      </div>

      {/* Copy All Keys Button */}
      {hasAnyKeys && order.paymentStatus === 'paid' && (
        <div className="mb-6">
          <button
            onClick={() => copyAllKeys(order.items)}
            className="btn-secondary"
          >
            Copy All Keys
          </button>
        </div>
      )}

      {/* Order Items */}
      <div className="space-y-4">
        {order.items.map((item: any) => (
          <div key={item.id} className="card p-6">
            <div className="flex gap-4 mb-4">
              {/* Thumbnail */}
              <div className="w-20 h-20 bg-leather rounded overflow-hidden flex-shrink-0">
                {item.thumbnailUrl ? (
                  <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-gold/30 font-western">OO</span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1">
                <Link
                  to={`/products/${item.productId}`}
                  className="text-cream hover:text-gold transition-colors font-semibold"
                >
                  {item.productName}
                </Link>
                <div className="text-text-secondary text-sm mt-1">
                  Qty: {item.quantity} &times; {formatCurrency(item.productPrice)}
                </div>
                <div className="text-gold font-semibold mt-1">
                  {formatCurrency(item.subtotal)}
                </div>
              </div>

              {/* Seller */}
              <div className="text-right text-sm">
                <span className="text-text-muted">Sold by</span>
                <Link
                  to={`/sellers/${item.seller.storeSlug}`}
                  className="block text-text-secondary hover:text-cream"
                >
                  {item.seller.storeName}
                </Link>
              </div>
            </div>

            {/* Delivery Status */}
            <div className="border-t border-gold/10 pt-4">
              {item.deliveryStatus === 'delivered' ? (
                <div className="text-success text-sm mb-3">
                  Delivered {item.deliveredAt && `on ${new Date(item.deliveredAt).toLocaleDateString()}`}
                </div>
              ) : (
                <div className="text-gold text-sm mb-3">
                  Delivery pending
                </div>
              )}

              {/* Keys Display */}
              {item.keys && item.keys.length > 0 ? (
                <div className="bg-background rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-text-muted text-sm">
                      Your {item.keys.length > 1 ? `${item.keys.length} Keys` : 'Key'}
                    </span>
                    <button
                      onClick={() => copyToClipboard(item.keys.join('\n'))}
                      className="text-gold hover:text-gold-bright text-sm"
                    >
                      Copy {item.keys.length > 1 ? 'All' : ''}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {item.keys.map((key: string, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-leather rounded px-3 py-2 group"
                      >
                        <code className="text-cream font-mono text-sm break-all">
                          {key}
                        </code>
                        <button
                          onClick={() => copyToClipboard(key)}
                          className="text-text-muted hover:text-gold ml-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Copy"
                        >
                          &#128203;
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : order.paymentStatus === 'paid' ? (
                <div className="text-text-secondary text-sm">
                  Check your downloads for this product.
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
